import type React from "react";
import { i18n } from "@frontend/i18n";

type CreditSide = "left" | "right" | "alone";

interface EventCreditsProps {
  readonly eventName: string;
  readonly logoUrl: string | null;
  readonly hasLogo: boolean;
  readonly onLogoError: () => void;
  readonly sponsorLogoSrc: string | null;
  readonly sponsorName: string;
  readonly hasSponsor: boolean;
  readonly onSponsorError: () => void;
}

/**
 * The join page's foot credits: the host's logo, and a sponsor's beside it when
 * the event has one.
 *
 * Presentational: the page owns each mark's failure flag, because the same
 * answer decides whether the foot renders at all and what the venue screen
 * credits. The two flags stay separate so a dead sponsor link cannot erase a
 * good host credit.
 */
export function EventCredits({
  eventName,
  logoUrl,
  hasLogo,
  onLogoError,
  sponsorLogoSrc,
  sponsorName,
  hasSponsor,
  onSponsorError,
}: EventCreditsProps): React.JSX.Element | null {
  if (!hasLogo && !hasSponsor) return null;

  const bothShown = hasLogo && hasSponsor;
  // Two credits share the foot as equal halves split by a rule; one credit
  // keeps the single centred column the foot already had.
  const groupCssClass = bothShown
    ? "grid w-full grid-cols-2 divide-x divide-border"
    : "flex flex-col items-center gap-2 lg:flex-row lg:gap-2.5";

  return (
    <div className={groupCssClass}>
      {hasLogo && logoUrl !== null && (
        <Credit
          label={i18n.t("user.eventLanding.organizedBy")}
          src={logoUrl}
          alt={eventName}
          side={bothShown ? "left" : "alone"}
          onError={onLogoError}
        />
      )}
      {hasSponsor && sponsorLogoSrc !== null && (
        <Credit
          label={i18n.t("user.eventLanding.supportedBy")}
          src={sponsorLogoSrc}
          alt={sponsorName}
          side={bothShown ? "right" : "alone"}
          onError={onSponsorError}
        />
      )}
    </div>
  );
}

function Credit({
  label,
  src,
  alt,
  side,
  onError,
}: {
  readonly label: string;
  readonly src: string;
  readonly alt: string;
  readonly side: CreditSide;
  readonly onError: () => void;
}): React.JSX.Element {
  const sideCssClass =
    side === "alone"
      ? "lg:flex-row lg:gap-2.5"
      : side === "left"
        ? "pr-4"
        : "pl-4";

  return (
    <div
      className={`flex min-w-0 flex-col items-center gap-2 [@media(max-height:640px)]:gap-1.5 ${sideCssClass}`}
    >
      <p className="text-[11px] leading-none text-muted-foreground [@media(max-height:640px)]:text-[10px]">
        {label}
      </p>
      <div className="flex h-10 items-center [@media(max-height:640px)]:h-8">
        {/* The height cap lets a stacked mark grow; the width cap keeps a wide
          one-line mark near its old 24px-tall size and shrinks it into its
          half rather than pushing the rule off-centre. */}
        <img
          src={src}
          alt={alt}
          onError={onError}
          className="max-h-10 w-auto max-w-[min(100%,160px)] object-contain [@media(max-height:640px)]:max-h-8 [@media(max-height:640px)]:max-w-[min(100%,128px)]"
        />
      </div>
    </div>
  );
}
